import { supabase } from '../supabaseClient';

export interface ZohoCustomer {
  contact_id: string;
  contact_name: string;
  company_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  status?: string;
  currency_code?: string;
  billing_address?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
}

interface ListResponse {
  customers: ZohoCustomer[];
  page_context: {
    page?: number;
    per_page?: number;
    has_more_page?: boolean;
    total?: number;
  };
}

interface ImportResponse {
  client: {
    id: string;
    name: string;
    [key: string]: unknown;
  };
  contacts_imported: number;
  contacts_skipped: number;
}

export interface SyncResponse {
  synced: boolean;
  changes: string[];
  new_contacts: number;
  reason?: string;
}

export interface ZohoEstimate {
  estimate_id: string;
  estimate_number?: string;
  customer_id?: string;
  customer_name?: string;
  subject?: string;
  reference_number?: string;
  date?: string;
  expiry_date?: string;
  status?: string;
  total?: number;
  currency_symbol?: string;
}

interface ListEstimatesResponse {
  estimates: ZohoEstimate[];
  page_context: {
    page?: number;
    per_page?: number;
    has_more_page?: boolean;
    total?: number;
  };
}

export interface EstimatePrefill {
  clientName?: string;
  title?: string;
  deliverables?: Array<{ id: string; title: string; description: string }>;
  service_cost?: number;
  expirationDate?: string;
  estimatedStartDate?: string;
  executiveSummary?: string;
  metadata?: Record<string, unknown>;
}

export interface ImportEstimateResponse {
  client: { id: string; name: string } | null;
  prefill: EstimatePrefill;
  estimate_number?: string;
  estimate_id: string;
}

async function authHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

export const zohoBooksService = {
  async listCustomers(opts: { search?: string; page?: number } = {}): Promise<ListResponse> {
    const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zoho-books-list-customers`);
    if (opts.search) url.searchParams.set('search', opts.search);
    if (opts.page) url.searchParams.set('page', String(opts.page));

    const res = await fetch(url.toString(), { headers: await authHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to list Zoho Books customers');
    }
    return res.json();
  },

  async importCustomer(zohoContactId: string): Promise<ImportResponse> {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zoho-books-import-customer`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ zoho_contact_id: zohoContactId }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to import customer');
    }
    return res.json();
  },

  async listEstimates(opts: { customerId?: string; search?: string; page?: number } = {}): Promise<ListEstimatesResponse> {
    const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zoho-books-list-estimates`);
    if (opts.customerId) url.searchParams.set('customer_id', opts.customerId);
    if (opts.search) url.searchParams.set('search', opts.search);
    if (opts.page) url.searchParams.set('page', String(opts.page));

    const res = await fetch(url.toString(), { headers: await authHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to list Zoho Books estimates');
    }
    return res.json();
  },

  async importEstimate(zohoEstimateId: string): Promise<ImportEstimateResponse> {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zoho-books-import-estimate`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ zoho_estimate_id: zohoEstimateId }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to import estimate');
    }
    return res.json();
  },

  async syncCustomer(clientId: string): Promise<SyncResponse> {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zoho-books-sync-customer`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ client_id: clientId }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to sync customer');
    }
    return res.json();
  },
};
